<template>
  <div id="EmailVerificationModal">
    <b-modal
      id="email-verification-modal"
      size="lg"
      centered
      hide-footer
      hide-header
      no-close-on-backdrop
    >
      <div class="p-5" :class="appClass">
        <button class="close-icon" @click="dismiss" aria-label="Dismiss">
          <font-awesome-icon icon="times" />
        </button>
        <div v-if="step === 'prompt'" class="text-center">
          <h2 class="title">To continue, please verify your email address</h2>
          <p>To make sure we can reach you, please verify your email address.</p>
          <p>Click the button below and we'll send a confirmation link to {{ userEmail }}.</p>
          <b-button
            class="button btn-primary bg-brand-primary"
            :disabled="sending"
            @click="sendVerificationEmail"
          >
            Verify my email
          </b-button>
        </div>
        <div v-else class="text-center">
          <h2 class="title">Thank you</h2>
          <p>We have sent a confirmation link to {{ userEmail }}, please check your inbox.</p>
          <b-button class="button btn-primary bg-brand-primary" @click="dismiss">
            Close
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { isAffirmative } from "@/utils/isAffirmative/isAffirmative";
import { isEmbedded } from "@/utils/embed/embed";

export default {
  name: "EmailVerificationModal",
  data() {
    return {
      step: "prompt",
      sending: false,
    };
  },
  computed: {
    skin() {
      return this.$store.getters.skin;
    },
    appClass() {
      let className = 'theme-' + this.skin;
      if(this.darkMode) className += ' theme_dark_mode';
      return className
    },
    userEmail() {
      return this.$store.getters.user.user.email;
    },
    // The backend serializes this boolean inconsistently across environments
    // (real `true`/`false`, or `1`/`0`, or `null` for never-set accounts).
    // Treat only an affirmative signal as verified; anything else counts as
    // unverified so the prompt shows and HomeStartModal stays blocked.
    isEmailVerified() {
      return isAffirmative(this.$store.getters.emailVerified);
    },
  },
  async mounted() {
    // Scoped to a single login: a fresh tab (or reload) rehydrates with the
    // prompt already resolved, so it only shows in the tab where login happened.
    if (this.$store.getters.emailVerificationPromptResolved) {
      return;
    }
    if (isEmbedded()) {
      this.$store.commit("setEmailVerificationPromptResolved", true);
      return;
    }
    await this.$store.dispatch("checkEmailVerified");
    if (this.isEmailVerified) {
      this.$store.commit("setEmailVerificationPromptResolved", true);
    } else {
      this.$bvModal.show("email-verification-modal");
    }
  },
  methods: {
    async sendVerificationEmail() {
      this.sending = true;
      try {
        const success = await this.$store.dispatch("sendEmailVerification");
        if (success) {
          this.step = "sent";
        }
      } finally {
        this.sending = false;
      }
    },
    dismiss() {
      this.$bvModal.hide("email-verification-modal");
      this.$store.commit("setEmailVerificationPromptResolved", true);
    },
  },
};
</script>

<style lang="less">
#email-verification-modal {

  .modal-body {
    padding: 48px 32px;
    position: relative;
  }

  .modal-content {
    border-radius: 0;
  }

  // Taller on desktop to match the Figma; mobile keeps its natural
  // content-driven height so short screens don't force scrolling.
  @media (min-width: 768px) {
    .modal-dialog {
      max-width: 560px;
    }

    .modal-content {
      min-height: 480px;
    }

    .modal-body {
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 64px 32px;
    }
  }

  .close-icon {
    position: absolute;
    top: 16px;
    left: 16px;
    background: transparent;
    border: none;
    font-size: 1.8rem;
    color: #333333;
    padding: 8px;
  }

  .title {
    font-size: 2.4rem;
    font-weight: bold;
    margin-bottom: 24px;
    text-align: center;
  }

  p {
    font-size: 1.6rem;
    margin-bottom: 16px;
  }

  .button {
    margin-top: 24px;
    width: 250px !important;
    border-radius: 5px !important;
    padding: 14px 32px;
    color: #ffffff;
    font-size: 1.6rem;
    border: none;
  }

  @media (max-width: 576px) {
    .modal-body {
      padding: 40px 20px;
    }
  }
}
</style>
